import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { BrandedLoading } from "@/components/BrandedLoading";
import StarField from "@/components/StarField";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Meet from "@/pages/Meet";
import MeetSession from "@/pages/MeetSession";
import Save from "@/pages/Save";
import Dashboard from "@/pages/Dashboard";
import Home from "@/pages/Home";
import Characters from "@/pages/Characters";
import CompanionPage from "@/pages/CompanionPage";
import Rest from "@/pages/Rest";
import ComingSoon from "@/pages/ComingSoon";
import SessionPlayer from "@/pages/SessionPlayer";
import Settings from "@/pages/Settings";
import Pricing from "@/pages/Pricing";
import ResetPassword from "@/pages/ResetPassword";
import LegalPage from "@/pages/LegalPage";
import CompanionPicker from "@/pages/CompanionPicker";
import { SessionHost } from "@/components/SessionHost";
import MorningAutoStart from "@/components/MorningAutoStart";
import { initNativeLifecycle } from "@/lib/native";

// Iter 96 — native lifecycle bridge (no-op in the browser).
initNativeLifecycle();
import DeveloperPanel, {
  DeveloperRoute,
  DevSessionDiagnostics,
  DevGenerationMetrics,
  DevPromptInspector,
  DevAudioPipeline,
  DevMemoryInspector,
  DevInnerLife,
  DevSubscriptions,
  DevProductAnalytics,
  DevTestingControls,
} from "@/pages/DeveloperPanel";
import "@/App.css";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <BrandedLoading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <BrandedLoading />;
  if (user) return <Navigate to="/app" replace />;
  return children;
};

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          theme="dark"
          position="top-center"
          offset={16}
          toastOptions={{
            style: {
              // Push toasts below the iOS Dynamic Island / notch.
              marginTop: "env(safe-area-inset-top, 0px)",
            },
          }}
        />
        {/* Persistent night sky — mounted ONCE for the whole app session so the
            "stars waking up" animation plays only on cold start and never
            restarts on navigation. The UI moves above this one continuous sky. */}
        <StarField />
        <Routes>
          <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/legal/privacy" element={<LegalPage doc="privacy" />} />
          <Route path="/legal/terms" element={<LegalPage doc="terms" />} />
          <Route path="/legal/ai-disclaimer" element={<LegalPage doc="ai-disclaimer" />} />
          <Route path="/legal/support" element={<LegalPage doc="support" />} />
          <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
          <Route path="/meet" element={<PublicOnly><Meet /></PublicOnly>} />
          <Route path="/meet/session" element={<PublicOnly><MeetSession /></PublicOnly>} />
          <Route path="/save" element={<PublicOnly><Save /></PublicOnly>} />
          <Route path="/app" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/characters" element={<ProtectedRoute><Characters /></ProtectedRoute>} />
          <Route path="/companion/:id" element={<ProtectedRoute><CompanionPage /></ProtectedRoute>} />
          <Route path="/rest" element={<ProtectedRoute><Rest /></ProtectedRoute>} />
          {/* Iter 74 — Adventure route removed. Any /adventure deep links redirect
              to /rest (Sleep). Adventure returns in Continua 2.0. */}
          <Route path="/adventure" element={<Navigate to="/rest" replace />} />
          {/* Iter 121 — Morning lives inside Sessions now. Old deep links land
              on the Sessions screen with the Morning tab preselected. */}
          <Route path="/morning" element={<Navigate to="/rest?mode=morning" replace />} />
          <Route path="/coming-soon" element={<ProtectedRoute><ComingSoon /></ProtectedRoute>} />
          {/* Iter 86 — /waiting is rendered by the persistent SessionHost above. */}
          <Route path="/waiting" element={null} />
          <Route path="/sessions/:id" element={<ProtectedRoute><SessionPlayer /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/upgrade" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
          <Route path="/companion" element={<ProtectedRoute><CompanionPicker /></ProtectedRoute>} />
          {/* Iter 84 — hidden developer routes. For non-developers these
              silently redirect to "/", indistinguishable from the "*" route. */}
          <Route path="/dev" element={<DeveloperRoute><DeveloperPanel /></DeveloperRoute>} />
          <Route path="/dev/analytics" element={<DeveloperRoute><DevProductAnalytics /></DeveloperRoute>} />
          <Route path="/dev/testing" element={<DeveloperRoute><DevTestingControls /></DeveloperRoute>} />
          <Route path="/dev/sessions" element={<DeveloperRoute><DevSessionDiagnostics /></DeveloperRoute>} />
          <Route path="/dev/metrics" element={<DeveloperRoute><DevGenerationMetrics /></DeveloperRoute>} />
          <Route path="/dev/prompts" element={<DeveloperRoute><DevPromptInspector /></DeveloperRoute>} />
          <Route path="/dev/audio" element={<DeveloperRoute><DevAudioPipeline /></DeveloperRoute>} />
          <Route path="/dev/memory" element={<DeveloperRoute><DevMemoryInspector /></DeveloperRoute>} />
          <Route path="/dev/inner-life" element={<DeveloperRoute><DevInnerLife /></DeveloperRoute>} />
          <Route path="/dev/subscriptions" element={<DeveloperRoute><DevSubscriptions /></DeveloperRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* Iter 86 — GLOBAL PLAYBACK: the active session lives here, outside
            the route tree, so navigating through Continua never interrupts
            Serine. The /waiting route above is only a URL slot; the actual
            session view is rendered (and persisted) by SessionHost. Rendered
            AFTER Routes so hidden session nodes come last in DOM order. */}
        <SessionHost />
        {/* Iter 92 — automatic Morning Sessions scheduler (overnight + on-open fallback). */}
        <MorningAutoStart />
      </BrowserRouter>
    </AuthProvider>
  );
}
